/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { DeliveryOptionsCartLineAddress } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import React, { useState } from 'react';

export interface ICheckoutCartLineDeliveryDateProps {
    className?: string;
    isDisabled?: boolean;
    minRangeValue?: string;
    maxRangeValue?: string;
    validationErrorForRange?: string;
    fallbackPattern?: string;
    ariaLabel?: string;
    telemetryContent?: ITelemetryContent;
    disclaimerText?: string;
    heading?: string;
    checkBoxLabel?: string;
    onChangeMinValue?: void;
    id?: string;
    cartLines?: CartLine[];
    groupDeliveryOption?: DeliveryOptionsCartLineAddress;
    onChange(event: React.ChangeEvent<HTMLInputElement>, cartLines: CartLine[]): void;
}

const CheckoutCartLineDeliveryDate: React.FC<ICheckoutCartLineDeliveryDateProps> = ({
    className,
    isDisabled,
    fallbackPattern,
    ariaLabel,
    telemetryContent,
    onChangeMinValue,
    checkBoxLabel,
    disclaimerText,
    heading,
    cartLines,
    groupDeliveryOption,
    onChange
}) => {
    const [selectedDate, setSelectedDate] = useState('');
    const [isDateEnabled, setIsDateEnabled] = useState(false);
    // State to store today's date
    const [minDate, setMinDate] = useState('');

    // Update minDate state with today's date on component mount
    useState(() => {
        const today = new Date().toISOString().split('T')[0];
        setMinDate(today);
    });

    const handleDateChange = (event: React.FocusEvent<HTMLInputElement>) => {
        const selectedValue = event.target.value;
        // Check if the selected date is allowed
        if (selectedValue >= minDate) {
            setSelectedDate(selectedValue);
            console.log('Selected date is allowed.', selectedValue);
        } else {
            console.log('Selected date is not allowed.');
        }

        if (groupDeliveryOption !== undefined && cartLines !== undefined) {
            const cartLinesDate: CartLine[] = [];
            groupDeliveryOption?.cartLineIds?.map(cartLineId => {
                const tempCartLine = cartLines?.find(cartLine => cartLineId === cartLine?.LineId?.toString());
                if (tempCartLine !== undefined) {
                    cartLinesDate.push(tempCartLine);
                }
            });

            if (cartLinesDate !== undefined) {
                onChange(event, cartLinesDate);
            }
        }
    };

    const handleCheckboxChange = () => {
        setIsDateEnabled(!isDateEnabled);
    };

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.DeliveryOptions);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <div className={className}>
            <div>
                <input
                    className={`${className}__checkbox`}
                    type='checkbox'
                    aria-label={ariaLabel}
                    name='requestDeliveryDate'
                    checked={isDateEnabled}
                    onChange={handleCheckboxChange}
                />
                <span className={`${className}__lbl`}>{checkBoxLabel}</span>
            </div>
            <span className={`${className}__disclaimer`}>{disclaimerText}</span>
            <div className={`${className}__calender`}>
                <span className={`${className}__calender-h`}>{heading}</span>
                <input
                    className={`${className}__calender-c`}
                    type='date'
                    name='dateInput'
                    value={selectedDate}
                    onChange={handleDateChange}
                    // Optionally, you can set min and max attributes based on your allowed dates range
                    min={minDate}
                    pattern={fallbackPattern}
                    {...attributes}
                    disabled={isDateEnabled ? isDisabled : true}
                />
            </div>
        </div>
    );
};

export default CheckoutCartLineDeliveryDate;
